import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import apiClient from "../../api/apiClient";
import { toast } from "react-toastify";
import { Role, User } from "../../features/users/usersSlice";
import { Company } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";
import { Icon } from "../common/Icon";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
  FaUserTag,
  FaSave,
  FaTimes,
} from "react-icons/fa";

interface UserFormModalProps {
  show: boolean;
  onHide: (refresh?: boolean) => void;
  user: User | null;
  roles: Role[];
  companies: Company[];
  agencyId?: number;
}

interface UserFormData {
  username: string;
  email: string;
  full_name: string;
  password?: string;
  status: string;
  role_ids: string[];
  company_ids: number[];
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  show,
  onHide,
  user,
  roles,
  companies,
  agencyId,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    full_name: "",
    password: "",
    status: "active",
    role_ids: [],
    company_ids: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(true);
  const [selectedCompanies, setSelectedCompanies] = useState<
    { id: number; role?: string }[]
  >([]);

  // Kullanıcı bilgilerini al
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const currentUserRoles = currentUser?.roles || [];
  const isAgencyOwner = currentUserRoles.includes('agency_owner');
  const isAdmin = currentUserRoles.includes('admin') || currentUserRoles.includes('superadmin');
  const isCompanyOwner = currentUserRoles.includes('company_owner');

  const isDesktop = useMediaQuery('(min-width: 992px)');
  const isEditMode = !!user;

  // Kullanıcı değiştiğinde formu güncelle
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        status: user.status,
        role_ids: user.roles,
        company_ids: user.companies.map((company) => company.company_id),
      });

      setSelectedCompanies(
        user.companies.map((company) => ({
          id: company.company_id,
          role: company.role,
        }))
      );

      setPasswordRequired(false);
    } else {
      setFormData({
        username: "",
        email: "",
        full_name: "",
        password: "",
        status: "active",
        role_ids: [],
        company_ids: [],
      });

      setSelectedCompanies([]);
      setPasswordRequired(true);
    }

    setError(null);
  }, [user, show]);

  // Agency Owner için otomatik rol ve şirket seçimi
  useEffect(() => {
    if (!user && show && roles.length > 0 && companies.length > 0 && isAgencyOwner) {
      const marketingSpecialistRole = roles.find(role => role.key === "marketing_specialist");
      const agency = companies.find(company => company.company_type === "agency");

      if (marketingSpecialistRole && agency) {
        setFormData(prev => ({
          ...prev,
          role_ids: [marketingSpecialistRole.key]
        }));

        setSelectedCompanies([{ id: agency.id, role: "member" }]);
      }
    }
  }, [user, show, roles, companies, isAgencyOwner]);

  // Company Owner için otomatik rol ve tek şirket seçimi
  useEffect(() => {
    if (!user && show && roles.length > 0 && companies.length === 1 && !isAgencyOwner && !isAdmin) {
      const companySpecialistRole = roles.find(role => role.key === "company_specialist");
      if (companySpecialistRole) {
        setFormData(prev => ({
          ...prev,
          role_ids: [companySpecialistRole.key]
        }));
      }
      setSelectedCompanies([{ id: companies[0].id, role: "member" }]);
    }
  }, [user, show, roles, companies, isAgencyOwner, isAdmin]);

  // Form alanlarını güncelle
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Rol seçimini güncelle
  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roleId = e.target.value;
    const isChecked = e.target.checked;

    if (isCompanyOwner) {
      // Company owner can choose exactly one role: company_specialist or personal
      setFormData((prev) => ({
        ...prev,
        role_ids: isChecked ? [roleId] : [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      role_ids: isChecked
        ? [...prev.role_ids, roleId]
        : prev.role_ids.filter((id) => id !== roleId),
    }));
  };

  // Şirket seçimini güncelle
  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const companyId = Number(e.target.value);
    if (companyId && !selectedCompanies.some((c) => c.id === companyId)) {
      setSelectedCompanies([...selectedCompanies, { id: companyId }]);
    }
  };

  // Şirketi kaldır
  const handleRemoveCompany = (companyId: number) => {
    setSelectedCompanies((prev) =>
      prev.filter((company) => company.id !== companyId)
    );
  };

  // Formu gönder
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        const updateUserData: any = {
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          status: formData.status,
          role_ids: formData.role_ids,
          company_ids: selectedCompanies.map((company) => company.id),
        };

        if (formData.password) {
          updateUserData.password = formData.password;
        }

        if (agencyId && user) {
          await usersService.updateAgencyUser(agencyId, user.id, updateUserData);
        } else {
          await apiClient.put(`/users/${user.id}`, updateUserData);
        }
        toast.success("User updated successfully");
      } else {
        let createUserData: any = {
          email: formData.email,
          full_name: formData.full_name,
          password: formData.password,
          status: formData.status,
          role_ids: formData.role_ids,
        };

        if (isAgencyOwner) {
          const agency = companies.find(company => company.company_type === "agency");
          createUserData.company_ids = agency ? [agency.id] : [];
        } else {
          createUserData.company_ids = selectedCompanies.map(company => company.id);
        }

        await apiClient.post("/users", createUserData);
        const successMessage = isAgencyOwner ? "Marketing Specialist created successfully" : "User created successfully";
        toast.success(successMessage);
      }

      onHide(true);
    } catch (error: any) {
      console.error("Error saving user:", error);
      setError(error.response?.data?.detail || "Failed to save user");
      toast.error("Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  // Şirket adını ID'ye göre bul
  const getCompanyName = (companyId: number) => {
    if (user && user.companies) {
      const userCompany = user.companies.find((c: any) => c.company_id === companyId);
      if (userCompany && userCompany.company_name) {
        return userCompany.company_name;
      }
    }

    const company = companies.find((c) => c.id === companyId);
    return company ? company.company_name : `Company ${companyId}`;
  };

  return (
    <Modal
      show={show}
      onHide={() => onHide()}
      size="lg"
      centered
      scrollable
      fullscreen="sm-down"
      className="modern-modal user-form-modal"
    >
      <Form onSubmit={handleSubmit} className="d-flex flex-column h-100">
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            <div className="header-icon-wrapper p-2 rounded-3 bg-soft-primary text-primary">
              <Icon icon={isEditMode ? FaUser : FaUserTag} size="1.2rem" />
            </div>
            {isEditMode
              ? "Edit User"
              : ((isAgencyOwner || isCompanyOwner) ? "Add Member" : "Add User")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modern-modal-body">
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-2">
                  <Icon icon={FaUser} size="0.9rem" className="text-muted" />
                  Full Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter full name"
                  className="form-control-modern"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-2">
                  <Icon icon={FaEnvelope} size="0.9rem" className="text-muted" />
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter email address"
                  className="form-control-modern"
                />
              </Form.Group>
            </Col>
          </Row>

          {!isEditMode && (
            <div className="bg-soft-secondary p-3 rounded-3 mt-4 mb-3">
              <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                <Icon icon={FaLock} /> Security
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={passwordRequired}
                      placeholder="Enter password"
                      className="form-control-modern"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      onChange={handleChange}
                      required={passwordRequired}
                      placeholder="Confirm password"
                      className="form-control-modern"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {isEditMode && (
            <div className="bg-soft-secondary p-3 rounded-3 mt-4 mb-3">
              <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                <Icon icon={FaLock} /> Security
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>New Password (leave blank to keep current)</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className="form-control-modern"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          <div className="section-divider my-4 border-top"></div>

          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center gap-2 mb-3">
              <Icon icon={FaUserTag} size="0.9rem" className="text-muted" />
              Roles <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {((isCompanyOwner
                ? roles.filter((role) => role.key === "company_specialist" || role.key === "personal")
                : isAdmin
                ? [
                  ...roles,
                  ...(!roles.some(r => r.key === 'company_owner')
                    ? [{ id: -1001 as unknown as number, key: 'company_owner', name: 'Company Owner' } as Role]
                    : []),
                  ...(!roles.some(r => r.key === 'company_specialist')
                    ? [{ id: -1002 as unknown as number, key: 'company_specialist', name: 'Company Specialist' } as Role]
                    : []),
                  ...(!roles.some(r => r.key === 'personal')
                    ? [{ id: -1003 as unknown as number, key: 'personal', name: 'Personal' } as Role]
                    : []),
                ]
                : roles)).map((role) => (
                  <div key={role.key} className={`role-card p-2 border rounded ${formData.role_ids.includes(role.key) ? 'border-primary bg-soft-primary' : ''}`}>
                    <Form.Check
                      type={isCompanyOwner ? "radio" : "checkbox"}
                      name={isCompanyOwner ? "company_owner_role_choice" : undefined}
                      id={`role-${role.key}`}
                      label={role.name}
                      value={role.key}
                      checked={formData.role_ids.includes(role.key)}
                      onChange={handleRoleChange}
                      disabled={!isEditMode && isAgencyOwner && role.key === "marketing_specialist"}
                      className="mb-0"
                    />
                  </div>
                ))}
            </div>
            {!isEditMode && isAgencyOwner && (
              <Form.Text className="text-muted d-block mt-2">
                <Icon icon={FaCheckCircle} className="text-success me-1" />
                Marketing Specialist role is automatically selected for new team members.
              </Form.Text>
            )}
          </Form.Group>

          {(isEditMode || (!isAgencyOwner && !isCompanyOwner)) && (
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center gap-2">
                <Icon icon={FaBuilding} size="0.9rem" className="text-muted" />
                Assign to Companies
              </Form.Label>
              <Form.Select
                value=""
                onChange={handleCompanyChange}
                className="form-control-modern mb-3"
              >
                <option value="">Select a company...</option>
                <optgroup label="Agency">
                  {companies
                    .filter((company) => company.company_type === "agency")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Client Companies">
                  {companies
                    .filter((company) => company.company_type === "client")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Single Companies">
                  {companies
                    .filter((company) => company.company_type === "single")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
              </Form.Select>

              {selectedCompanies.length > 0 && (
                <div className="selected-companies-list">
                  <h6 className="text-muted small text-uppercase fw-bold mb-2">Selected Companies</h6>
                  {selectedCompanies.map((company) => (
                    <div key={company.id} className="d-flex align-items-center justify-content-between mb-2 p-3 border rounded bg-light">
                      <div className="d-flex align-items-center gap-3">
                        <div className="company-icon bg-white p-2 rounded border">
                          <Icon icon={FaBuilding} className="text-primary" />
                        </div>
                        <div>
                          <div className="fw-bold">{getCompanyName(company.id)}</div>
                          <div className="text-muted small">
                            {(() => {
                              const type = companies.find(c => c.id === company.id)?.company_type;
                              if (type === "agency") return "Agency";
                              if (type === "client") return "Client Company";
                              if (type === "single") return "Single Company";
                              return "Company";
                            })()}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveCompany(company.id)}
                        className="btn-icon"
                      >
                        <Icon icon={FaTimes} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          )}

          {!isEditMode && isAgencyOwner && (
            <Alert variant="info" className="mt-3 d-flex align-items-center gap-2">
              <Icon icon={FaCheckCircle} size="1.2rem" />
              <div>
                <strong>Note:</strong> New marketing specialists will be automatically assigned to your agency with the Marketing Specialist role.
              </div>
            </Alert>
          )}

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="d-flex align-items-center gap-2">
                  {formData.status === 'active' ? (
                    <Icon icon={FaCheckCircle} size="0.9rem" className="text-success" />
                  ) : (
                    <Icon icon={FaTimesCircle} size="0.9rem" className="text-secondary" />
                  )}
                  Status
                </Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control-modern"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end px-4">

          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              className="btn-minimal"
              onClick={() => onHide()}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="info"
              className="btn-minimal"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("saving")}
                </>
              ) : (
                <>
                  <Icon icon={FaSave} className="me-2" />
                  {isEditMode
                    ? t("update_user")
                    : t("save")}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserFormModal;
